import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert, Button } from 'reactstrap';
import config from '../../config';
import CustomerDetailsModal from '../customers/CustomerDetailsModal';
import s from './KnowledgeGaps.module.scss';

const POLLING_INTERVAL = 30000; // 30s
const refreshIntervalSeconds = POLLING_INTERVAL / 1000;

const GAP_TYPE_LABELS = {
  missing_kb: 'No KB Found',
  low_confidence: 'Low Confidence',
  ambiguous: 'Ambiguous Match',
};

const STATUS_LABELS = {
  new: 'New',
  in_review: 'In Review',
  resolved: 'Resolved',
  declined: 'Declined',
};

export default function KnowledgeGaps() {
  const [allGaps, setAllGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('new');
  const [resolveModal, setResolveModal] = useState({ open: false, gap: null });
  const [resolveForm, setResolveForm] = useState({ resolution_type: 'general', approved_answer: '', updated_question: '' });
  const [resolving, setResolving] = useState(false);
  const [nextRefreshIn, setNextRefreshIn] = useState(refreshIntervalSeconds);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerModalId, setCustomerModalId] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchGaps = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.customerApiUrl}/knowledge-gaps`, {
        signal: abortControllerRef.current.signal,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });
      const data = await res.json();
      setAllGaps(data.gaps || []);
      setNextRefreshIn(refreshIntervalSeconds);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError('Failed to load knowledge gaps');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGaps(); }, [fetchGaps]);

  // Auto-refresh with polling
  useEffect(() => {
    const interval = setInterval(fetchGaps, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchGaps]);

  // Countdown timer
  useEffect(() => {
    if (loading) return;
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [loading]);

  const stats = {
    new: allGaps.filter(g => g.status === 'new').length,
    in_review: allGaps.filter(g => g.status === 'in_review').length,
    resolved: allGaps.filter(g => g.status === 'resolved').length,
    declined: allGaps.filter(g => g.status === 'declined').length,
    total: allGaps.length,
  };

  const filteredGaps = activeFilter === 'all'
    ? allGaps
    : allGaps.filter(g => g.status === activeFilter);

  const openResolve = (gap) => {
    setResolveModal({ open: true, gap });
    setResolveForm({ resolution_type: 'general', approved_answer: '', updated_question: (gap.english_question || gap.question_text || '') });
  };

  const handleResolve = async () => {
    if (!resolveForm.approved_answer.trim()) return;
    setResolving(true);
    try {
      const res = await fetch(
        `${config.customerApiUrl}/knowledge-gaps/${resolveModal.gap.id}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            resolution_type: resolveForm.resolution_type,
            approved_answer: resolveForm.approved_answer,
            updated_question: resolveForm.updated_question,
          }),
        }
      );
      if (res.ok) {
        setResolveModal({ open: false, gap: null });
        fetchGaps();
      }
    } catch (e) {
      console.error('Resolve failed:', e);
    } finally {
      setResolving(false);
    }
  };

  const markInReview = async (gap) => {
    try {
      await fetch(`${config.customerApiUrl}/knowledge-gaps/${gap.id}/in-review`, { method: 'POST' });
      fetchGaps();
    } catch (e) {
      console.error('Mark in-review failed:', e);
    }
  };

  const handleDeleteGap = async (gap) => {
    if (!window.confirm(`Delete gap "${gap.question_text.substring(0, 60)}..." permanently?`)) return;
    try {
      await fetch(`${config.customerApiUrl}/knowledge-gaps/${gap.id}`, { method: 'DELETE' });
      fetchGaps();
    } catch (e) {
      console.error('Delete gap failed:', e);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={s.root}>
      <div className={s.pageBody}>
        {/* Header */}
        <div className={s.header}>
          <div className={s.headerLeft}>
            <h3 className={s.headerTitle}>Knowledge Gaps</h3>
            <p className={s.headerSubtitle}>Unanswered questions that need ops attention</p>
          </div>
          <div className={s.headerRight}>
            <button
              className={s.refreshBtn}
              onClick={fetchGaps}
              disabled={loading}
              title={`Auto-refreshing in ${nextRefreshIn}s`}
            >
              <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
              {!loading && (
                <div className={s.refreshProgressTrack}>
                  <div
                    className={s.refreshProgressBar}
                    style={{ width: `${Math.max(0, Math.min(100, (nextRefreshIn / refreshIntervalSeconds) * 100))}%` }}
                  />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Stats Row — clickable filters */}
        <div className={s.statsRow}>
          <div
            className={`${s.statCard} ${s.new} ${s.clickable} ${activeFilter === 'new' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('new')}
            role="button"
            title="Show new gaps"
          >
            <div className={`${s.statIcon} ${s.new}`}>!</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.new}</div>
              <div className={s.statLabel}>New</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.in_review} ${s.clickable} ${activeFilter === 'in_review' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('in_review')}
            role="button"
            title="Show in review gaps"
          >
            <div className={`${s.statIcon} ${s.in_review}`}>~</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.in_review}</div>
              <div className={s.statLabel}>In Review</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.resolved} ${s.clickable} ${activeFilter === 'resolved' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('resolved')}
            role="button"
            title="Show resolved gaps"
          >
            <div className={`${s.statIcon} ${s.resolved}`}>&#10003;</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.resolved}</div>
              <div className={s.statLabel}>Resolved</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.declined} ${s.clickable} ${activeFilter === 'declined' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('declined')}
            role="button"
            title="Show declined gaps"
          >
            <div className={`${s.statIcon} ${s.declined}`}>&#10007;</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.declined}</div>
              <div className={s.statLabel}>Declined</div>
            </div>
          </div>
          <div
            className={`${s.statCard} ${s.total} ${s.clickable} ${activeFilter === 'all' ? s.filterActive : ''}`}
            onClick={() => setActiveFilter('all')}
            role="button"
            title="Show all gaps"
          >
            <div className={`${s.statIcon} ${s.total}`}>&#9776;</div>
            <div className={s.statContent}>
              <div className={s.statValue}>{stats.total}</div>
              <div className={s.statLabel}>Total</div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && <Alert color="danger">{error}</Alert>}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : filteredGaps.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>&#10003;</div>
            <h5 className={s.emptyTitle}>No knowledge gaps</h5>
            <p className={s.emptyText}>All questions are being answered by the knowledge base.</p>
          </div>
        ) : (
          <div className={s.gapsList}>
            {filteredGaps.map((gap) => (
              <div key={gap.id} className={`${s.gapCard} ${gap.status === 'resolved' ? s.resolved : ''}`}>
                <div className={s.gapHeader}>
                  <div style={{ flex: 1 }}>
                    <p className={s.gapQuestion}>{gap.english_question || gap.question_text}</p>
                    {gap.english_question
                      && gap.english_question.trim().toLowerCase()
                        !== (gap.question_text || '').trim().toLowerCase()
                      && <p className={s.gapOriginal}>{gap.question_text}</p>
                    }
                    <div className={s.gapMeta}>
                      <span
                        className={s.metaItem}
                        onClick={gap.customer_id ? (e) => { e.stopPropagation(); setCustomerModalId(gap.customer_id); setCustomerModalOpen(true); } : undefined}
                        style={gap.customer_id ? { cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' } : undefined}
                        title={gap.customer_id ? 'View customer details' : undefined}
                      >
                        {gap.business_name || gap.user_name || (gap.user_phone ? `+${gap.user_phone}` : 'Unknown')}
                      </span>
                      <span className={s.metaItem}>{formatDate(gap.created_at)}</span>
                      {gap.conversation_id && (
                        <span className={s.metaItem} title="Conversation where this gap was logged">
                          Conv #{gap.conversation_id}
                        </span>
                      )}
                      {gap.gap_type && gap.gap_type !== 'missing_kb' && (
                        <span className={`${s.badge} ${s.gapType}`}>{GAP_TYPE_LABELS[gap.gap_type] || gap.gap_type}</span>
                      )}
                      {gap.retrieval_score != null && (
                        <span className={s.metaItem}>Score: {(gap.retrieval_score * 100).toFixed(0)}%</span>
                      )}
                      <span className={`${s.badge} ${s[gap.status] || ''}`}>{STATUS_LABELS[gap.status] || gap.status}</span>
                    </div>
                  </div>
                  <div className={s.gapActions}>
                    {gap.status === 'new' && (
                      <>
                        <button className={s.markReviewBtn} onClick={() => markInReview(gap)}>
                          In Review
                        </button>
                        <button className={s.resolveBtn} onClick={() => openResolve(gap)}>
                          Resolve
                        </button>
                      </>
                    )}
                    {gap.status === 'in_review' && (
                      <button className={s.resolveBtn} onClick={() => openResolve(gap)}>
                        Resolve
                      </button>
                    )}
                    {gap.status !== 'resolved' && gap.status !== 'declined' && (
                      <button className={s.deleteGapBtn} onClick={() => handleDeleteGap(gap)}>
                        Delete
                      </button>
                    )}
                    {gap.status === 'resolved' && (
                      <span className={`${s.badge} ${s.resolved}`}>Resolved</span>
                    )}
                    {gap.status === 'declined' && (
                      <span className={`${s.badge} ${s.declined}`}>Declined</span>
                    )}
                  </div>
                </div>
                {gap.approved_answer && (
                  <div className={s.gapAnswer}>
                    <div className={s.answerLabel}>Approved Answer</div>
                    <div className={s.answerText}>{gap.approved_answer}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Resolve Modal */}
        <Modal isOpen={resolveModal.open} toggle={() => setResolveModal({ open: false, gap: null })} size="lg">
          <ModalHeader toggle={() => setResolveModal({ open: false, gap: null })}>
            Resolve Knowledge Gap
          </ModalHeader>
          <ModalBody>
            {resolveModal.gap && (
              <>
                <div className={s.questionPreview}>
                  <div className={s.questionPreviewLabel}>Original Customer Question</div>
                  <div className={s.questionPreviewText}>{resolveModal.gap.question_text}</div>
                  {resolveModal.gap.english_question
                    && resolveModal.gap.english_question.trim().toLowerCase()
                      !== (resolveModal.gap.question_text || '').trim().toLowerCase()
                    && <div className={s.questionPreviewOriginal}>English: {resolveModal.gap.english_question}</div>
                  }
                  <div className={s.questionPreviewSource}>
                    From: {resolveModal.gap.business_name || resolveModal.gap.user_name || (resolveModal.gap.user_phone ? `+${resolveModal.gap.user_phone}` : 'Unknown')}
                  </div>
                </div>
                <Form>
                  <FormGroup>
                    <Label>Polished Question (edit to clean up garbled text)</Label>
                    <Input
                      value={resolveForm.updated_question}
                      onChange={(e) => setResolveForm({ ...resolveForm, updated_question: e.target.value })}
                      placeholder="Clean up or rephrase the raw customer question..."
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Resolution Type</Label>
                    <Input
                      type="select"
                      value={resolveForm.resolution_type}
                      onChange={(e) => setResolveForm({ ...resolveForm, resolution_type: e.target.value })}
                    >
                      <option value="general">General — applies to all customers (stored in Knowledge Base)</option>
                      <option value="customer_specific">Customer-Specific — only for this customer</option>
                    </Input>
                  </FormGroup>
                  <FormGroup>
                    <Label>Approved Answer</Label>
                    <Input
                      type="textarea"
                      rows={5}
                      placeholder="Write the ops-approved answer here..."
                      value={resolveForm.approved_answer}
                      onChange={(e) => setResolveForm({ ...resolveForm, approved_answer: e.target.value })}
                    />
                    <small className="text-muted mt-2 d-block">
                      General answers are auto-ingested into the Knowledge Base for future questions.
                      Customer-specific answers are stored as per-customer memory facts.
                    </small>
                  </FormGroup>
                </Form>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setResolveModal({ open: false, gap: null })}>Cancel</Button>
            <Button
              color="primary"
              onClick={handleResolve}
              disabled={!resolveForm.approved_answer.trim() || resolving}
            >
              {resolving ? <Spinner size="sm" /> : 'Resolve & Store'}
            </Button>
          </ModalFooter>
        </Modal>

        <CustomerDetailsModal
          isOpen={customerModalOpen}
          toggle={() => setCustomerModalOpen(false)}
          customerId={customerModalId}
        />
      </div>
    </div>
  );
}
